import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { attendanceAPI } from '../../services/api';
import { AuthContext } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { reportLoadError } from '../../utils/notify';
import { can } from '../../utils/permissions';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import DayPanel from './DayPanel';
import { cellView, fmtHours, kindOf, payLabel } from './weekModel';
import styles from './TimeAttendance.module.css';

const fullName = (e) => `${e.first_name || ''} ${e.last_name || ''}`.trim();
const shiftMonth = (month, delta) => {
  const [y, m] = month.split('-').map(Number);
  const d = new Date(Date.UTC(y, m - 1 + delta, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
};
// Monday = 0 ... Sunday = 6
const weekdayIndex = (iso) => (new Date(`${iso}T00:00:00Z`).getUTCDay() + 6) % 7;

/**
 * Time & Attendance > Employee: one employee's month as a calendar (weeks
 * start on Monday), with the totals payroll uses, and the side panel to edit
 * a day. The employee and month are in the URL (?employee=7&month=2026-11).
 */
const EmployeeTab = () => {
  const { t, locale } = useLanguage();
  const tRef = useRef(t);
  tRef.current = t;
  const { user } = useContext(AuthContext);
  const canCreate = can(user, 'attendance', 'create');
  const canUpdate = canCreate && can(user, 'attendance', 'update');
  const canDelete = can(user, 'attendance', 'delete');

  const [params, setParams] = useSearchParams();
  const employeeId = params.get('employee') || '';
  const month = params.get('month') || '';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [panelDate, setPanelDate] = useState(null);

  const setQuery = (changes) => {
    const next = new URLSearchParams(params);
    next.set('tab', 'employee');
    Object.entries(changes).forEach(([k, v]) => (v ? next.set(k, v) : next.delete(k)));
    setParams(next);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const query = {};
      if (employeeId) query.employee_id = employeeId;
      if (month) query.month = month;
      const res = await attendanceAPI.getMonth(query);
      setData(res.data);
    } catch (error) {
      reportLoadError(error, tRef.current('Could not load the month'), { main: true });
    } finally {
      setLoading(false);
    }
  }, [employeeId, month]);

  useEffect(() => { load(); }, [load]);

  const recordsByDate = useMemo(() => {
    const map = new Map();
    (data?.records || []).forEach((r) => map.set(r.date, r));
    return map;
  }, [data]);

  const leaveDays = useMemo(() => (data?.records || []).reduce((acc, r) => {
    const kind = kindOf(r);
    if (kind === 'leave') acc.paid += 1;
    if (kind === 'unpaid') acc.unpaid += 1;
    return acc;
  }, { paid: 0, unpaid: 0 }), [data]);

  if (!data) {
    return (
      <div className={styles.empty}>
        {loading ? t('Loading...') : <button type="button" className="btn btn--secondary" onClick={load}>{t('Retry')}</button>}
      </div>
    );
  }

  const currentMonth = data.month;
  const employee = data.employee;
  const monthLabel = new Date(`${data.start}T00:00:00`).toLocaleDateString(locale, { month: 'long', year: 'numeric' });
  const blanks = data.days ? weekdayIndex(data.start) : 0;
  const weekdayNames = Array.from({ length: 7 }, (_, i) => new Date(Date.UTC(2026, 10, 23 + i)) // a Monday
    .toLocaleDateString(locale, { weekday: 'short', timeZone: 'UTC' }));
  const monthly = employee && employee.payment_type !== 'hourly';
  const totals = data.totals;
  const panelDay = panelDate && data.days ? data.days.find((d) => d.date === panelDate) : null;

  return (
    <div>
      <div className={styles.weekToolbar}>
        <label className={styles.field}>
          <span>{t('Employee')}</span>
          <select value={employee ? String(employee.id) : ''} onChange={(e) => setQuery({ employee: e.target.value })} className={`select ${styles.employeeSelect}`}>
            <option value="">{t('Choose an employee…')}</option>
            {data.employees.map((e) => (
              <option key={e.id} value={e.id}>
                {fullName(e)} — {e.payment_type === 'hourly' ? t('Hourly') : t('Monthly')} · {payLabel(e, t, locale)}
              </option>
            ))}
          </select>
        </label>
        <div className={styles.weekNav}>
          <button type="button" className="btn btn--secondary btn--icon" onClick={() => setQuery({ month: shiftMonth(currentMonth, -1) })} aria-label={t('Previous month')}><ChevronLeft aria-hidden="true" /></button>
          <span className={styles.weekRange}>{monthLabel}</span>
          <button type="button" className="btn btn--secondary btn--icon" onClick={() => setQuery({ month: shiftMonth(currentMonth, 1) })} aria-label={t('Next month')}><ChevronRight aria-hidden="true" /></button>
          <button type="button" className="btn btn--secondary" onClick={() => setQuery({ month: '' })} disabled={currentMonth === data.today.slice(0, 7)}>
            {t('This month')}
          </button>
        </div>
      </div>

      {!employee ? (
        <div className={styles.empty}>{t('Choose an employee to see their month.')}</div>
      ) : (
        <div className={styles.monthLayout}>
          <div className={styles.monthCalendar} role="group" aria-label={t('{name}, {month}', { name: fullName(employee), month: monthLabel })} aria-busy={loading}>
            <div className={styles.monthRow} aria-hidden="true">
              {weekdayNames.map((name, i) => (
                <div key={name} className={`${styles.monthHead} ${i === 6 ? styles.colSunday : ''}`}>{name}</div>
              ))}
            </div>
            <div className={styles.monthRow}>
              {Array.from({ length: blanks }, (_, i) => <div key={`blank-${i}`} className={styles.monthBlank} aria-hidden="true" />)}
              {data.days.map((d) => {
                const record = recordsByDate.get(d.date);
                const view = cellView(record, t);
                return (
                  <div key={d.date} className={`${styles.monthDay} ${d.is_holiday ? styles.colHoliday : d.is_sunday ? styles.colSunday : ''}`} data-kind={view.kind}>
                    <button
                      type="button"
                      className={`${styles.monthChip} ${view.kind === 'empty' ? styles.monthChipEmpty : styles[`chip_${view.kind}`]} ${d.date === data.today ? styles.monthToday : ''}`}
                      onClick={() => setPanelDate(d.date)}
                      aria-label={`${new Date(`${d.date}T00:00:00`).toLocaleDateString(locale, { weekday: 'long', day: 'numeric', month: 'long' })}: ${view.kind === 'empty' ? t('No record') : [view.line1, view.line2, ...view.flags].join(', ')}`}
                    >
                      <span className={styles.monthDayNumber}>
                        {Number(d.date.slice(8))}
                        {d.holiday_name && <span className={styles.monthHolidayName}> · {d.holiday_name}</span>}
                      </span>
                      {view.kind !== 'empty' && (
                        <>
                          <span className={styles.chipLine1}>{view.line1}</span>
                          <span className={styles.chipLine2}>{view.line2}</span>
                          {view.flags.map((f) => <span key={f} className={styles.chipFlag}>{f}</span>)}
                        </>
                      )}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>

          <aside className={styles.monthTotals} aria-label={t('Totals of the month')}>
            <h3 className={styles.sectionTitle}>{t('Totals of the month')}</h3>
            <p className={styles.panelHint}>{t('As payroll counts them (the same calculation).')}</p>
            <dl className={styles.totalsList}>
              <dt>{t('Days worked')}</dt>
              <dd>{totals.days_worked}</dd>
              <dt>{t('Hours worked')}</dt>
              <dd>{fmtHours(totals.total_hours_worked)}</dd>
              <dt className={styles.totalsSub}>{t('normal')}</dt>
              <dd className={styles.totalsSub}>{fmtHours(totals.normal_hours)}</dd>
              <dt className={styles.totalsSub}>{t('Sunday')}</dt>
              <dd className={styles.totalsSub}>{fmtHours(totals.sunday_hours)}</dd>
              <dt className={styles.totalsSub}>{t('Public holiday')}</dt>
              <dd className={styles.totalsSub}>{fmtHours(totals.holiday_hours)}</dd>
              <dt>{t('Overtime')}</dt>
              <dd>{monthly ? t('{minutes} min', { minutes: totals.overtime_minutes }) : fmtHours(totals.overtime_hours)}</dd>
              <dt>{t('Absent days')}</dt>
              <dd>{totals.absent_days}</dd>
              <dt>{t('Sick days')}</dt>
              <dd>{totals.sick_days}</dd>
              <dt>{t('Leave days')}</dt>
              <dd>{t('{paid} paid · {unpaid} unpaid', { paid: leaveDays.paid, unpaid: leaveDays.unpaid })}</dd>
              <dt>{t('Late')}</dt>
              <dd>{t('{minutes} min', { minutes: totals.total_late_minutes })}</dd>
            </dl>
            <p className={styles.panelHint}>
              {monthly
                ? t('Monthly employees: overtime is paid in minutes; their pay still counts the hours of every day.')
                : t('Hourly employees: hours count only for days present and paid leave.')}
            </p>
          </aside>
        </div>
      )}

      {employee && panelDay && (
        <DayPanel
          open
          employee={employee}
          day={panelDay}
          record={recordsByDate.get(panelDay.date)}
          leaveTypes={data.leave_types}
          standardHours={data.standard_hours}
          canCreate={canCreate}
          canUpdate={canUpdate}
          canDelete={canDelete}
          onClose={() => setPanelDate(null)}
          onSaved={() => {
            setPanelDate(null);
            load();
          }}
        />
      )}
    </div>
  );
};

export default EmployeeTab;
